// Arrow-key navigation works from DOM position (nearest <td> column, nearest
// row above/below with an <input> there) rather than tracked row/col indices,
// so it also handles PatternsEditor's wrapped-every-6-columns layout: Up/Down
// skips label-only rows and lands on the multiplier 6 positions away, which
// is also the correct next value given how that table wraps.

import type { KeyboardEvent } from "react";

/** Matches a real `<td>` (every caller but TimeSeriesEditor.tsx) or a `data-grid-cell` div (TimeSeriesEditor.tsx's own virtualized grid, which can't use a real `<table>` — see VirtualList.tsx's own header on why a `<div>` can't wrap a `<tr>`). */
const CELL_SELECTOR = "td, [data-grid-cell]";

function inputAt(row: Element, cellIndex: number): HTMLInputElement | null {
  const cell = row.children[cellIndex];
  return cell?.querySelector("input") ?? null;
}

function findInputInDirection(
  startRow: Element,
  cellIndex: number,
  direction: "previousElementSibling" | "nextElementSibling"
): HTMLInputElement | null {
  let row = startRow[direction];
  while (row) {
    const input = inputAt(row, cellIndex);
    if (input) return input;
    row = row[direction];
  }
  return null;
}

/** Up/Down move to the same column in the nearest row that has an input there; Left/Right move within the current row only (no wrap across rows). */
export function handleGridArrowNav(e: KeyboardEvent<HTMLInputElement>) {
  if (
    e.key !== "ArrowUp" &&
    e.key !== "ArrowDown" &&
    e.key !== "ArrowLeft" &&
    e.key !== "ArrowRight"
  ) {
    return;
  }
  const input = e.currentTarget;
  const cell = input.closest(CELL_SELECTOR);
  const row = cell?.parentElement;
  if (!cell || !row) return;
  const cellIndex = Array.from(row.children).indexOf(cell);

  let target: HTMLInputElement | null = null;
  if (e.key === "ArrowUp") target = findInputInDirection(row, cellIndex, "previousElementSibling");
  else if (e.key === "ArrowDown")
    target = findInputInDirection(row, cellIndex, "nextElementSibling");
  else {
    const rowInputs = Array.from(row.querySelectorAll("input"));
    const index = rowInputs.indexOf(input);
    target = rowInputs[e.key === "ArrowLeft" ? index - 1 : index + 1] ?? null;
  }

  if (target) {
    e.preventDefault();
    // Stops a virtualized caller's OWN outer Up/Down handler (VirtualList's
    // own row-to-row focus nav, TimeSeriesEditor.tsx) from also reacting to
    // an Up/Down this function already handled itself — the two would
    // otherwise both move focus, one on top of the other. Only reached when
    // a same-column target is found in the currently-mounted DOM (typically
    // true within the virtualized window's own overscan); past that,
    // nothing here fires and VirtualList's own coarser "first focusable
    // in the next mounted-or-scrolled-into-view row" nav takes over
    // instead, at the cost of losing "same column" for that one jump.
    e.stopPropagation();
    target.focus();
    target.select();
  }
}

/**
 * Deletes the whole row on Delete/Supr, but only when the field is already
 * empty with no selection — Delete pressed mid-word (or over a selection)
 * just deletes forward normally, the ordinary browser behavior, instead of
 * destroying the entire row. Every caller wires this to a plain
 * `type="text"` input (NumberInput's own buffered text input included, see
 * fields.tsx), so `.selectionStart`/`.selectionEnd` are always reliable
 * here — unlike a real `<input type="number">`, which never reports them
 * (always `null` in Chrome/Firefox) and this function used to be keyed on
 * Delete alone specifically to sidestep that. Not used by PatternsEditor,
 * whose row count is fixed by its Type rather than user-editable.
 */
export function handleGridDeleteRow(e: KeyboardEvent<HTMLInputElement>, onDeleteRow: () => void) {
  if (e.key !== "Delete") return;
  const input = e.currentTarget;
  if (input.value !== "" || input.selectionStart !== input.selectionEnd) return;
  e.preventDefault();
  onDeleteRow();
}
